import { useState, useEffect } from 'react';
import { Grid, Box, Flex, Text, Card, Button, AlertDialog, Badge } from '@radix-ui/themes';
import CircleProgress from '../shared/CircleProgress';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import StudentPlanCheck from './StudentPlanCheck';
import StudentQuiz from './StudentQuiz';
import { updateStudentQuiz } from '../../redux/slices/studentAccountSlice';
import { useDispatch, useSelector } from 'react-redux';

const CourseProgress = ({ activities, onQuizComplete }) => {
    console.log(activities);
    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const dispatch = useDispatch();
    const { course_info, quizzes, lessons } = activities;
    const studentAccount = useSelector((state) => state.studentAccount);
    const { quizzes: studentQuizzes } = studentAccount;

    console.log('studentQuizzes', studentQuizzes);

    const handleQuizComplete = async (quizAttempt) => {
        setSelectedQuiz(null);
        // Find the quiz and create a new copy with the updated attempts
        const updatedQuiz = quizzes.find(quiz => quiz.id === quizAttempt.quiz_id);
        if (!updatedQuiz) return;

        const newQuiz = {
            ...updatedQuiz,
            quiz_attempts: updatedQuiz.quiz_attempts
                ? [...updatedQuiz.quiz_attempts, quizAttempt]
                : [quizAttempt]
        };

        onQuizComplete?.(newQuiz);


        // Dispatch the update action
        dispatch(updateStudentQuiz(newQuiz));
    };


    // Format date range
    const startDate = new Date(course_info.period.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endDate = new Date(course_info.period.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    // Calculate average score from quiz attempts
    const averageScore = quizzes.reduce((sum, quiz) => {
        const attempts = quiz.quiz_attempts || [];
        if (attempts.length > 0) {
            return sum + attempts[0].percentage;
        }
        return sum;
    }, 0) / quizzes.filter(quiz => quiz.quiz_attempts?.length > 0).length || 0;

    // Prepare data for line chart
    const chartData = quizzes.map(quiz => ({
        score: quiz.quiz_attempts?.[0]?.percentage || 0,
        // name: quiz.lesson.title
        name: quiz.lesson.title.length > 20
            ? `${quiz.lesson.title.substring(0, 20)}...`
            : quiz.lesson.title,
    }));


    // if(paymentDetails.status !== 'active') {
    //     return (
    //         <Box p="4">
    //             <StudentPlanCheck />
    //         </Box>
    //     );
    // }

    return (
        <Box>
            <Flex direction="column" gap="4">
                <Flex justify="between" align="center">
                    <Box align="start">
                        <Text size="6" weight="bold" as="h1">{course_info.subject}</Text>
                        <Text size="2" color="gray" as="p">{`with ${course_info.teacher.name}`}</Text>
                    </Box>
                    <Flex direction="column" align="end">
                        <Text size="2">Period: {new Date().getFullYear()} | First Term</Text>
                        <Flex gap="2" align="start">
                            <Badge color="green">
                                <Text size="2">Ongoing</Text>
                            </Badge>
                            <Text size="2">{startDate} - {endDate}</Text>
                        </Flex>
                    </Flex>
                </Flex>

                {/* Main Content */}
                <Grid columns="2" gap="4">
                    {/* Quizzes Card */}
                    <Card>
                        <Flex direction="column" gap="4">
                            {/* Average Score Circle */}
                            <Flex
                                direction="column"
                                align="center"
                                justify="center"
                                p="4"
                            >
                                <Box style={{
                                    position: 'relative',
                                    width: '120px',
                                    height: '120px',
                                }}>
                                    <CircleProgress
                                        progress={averageScore}
                                        label={`${Math.round(averageScore)}%`}
                                        size={120}
                                        strokeWidth={5}
                                    />
                                </Box>
                                <Text size="2" color="gray" align="center">Average Score</Text>
                                <Text size="1" color="gray" align="center">Based on your first attempts</Text>
                            </Flex>

                            {/* Quizzes List */}
                            <Box>
                                <Text size="3" mb="3" weight="bold">Quizzes</Text>
                                {quizzes.map((quiz, index) => (
                                    <Flex key={quiz.id} align="between" gap="3" mb="3">
                                        <Box style={{ flex: 1 }}>
                                            <Text size="2">{quiz.lesson.title} | </Text>
                                            <Text size="2" color="gray">
                                                {quiz.quiz_attempts?.length || 0} Attempts
                                            </Text>
                                        </Box>
                                        {quiz.quiz_attempts?.length > 0 ? (
                                            <Text size="2" weight="bold">{quiz.quiz_attempts[0].percentage}%</Text>
                                        ) : (
                                            <Button size="1" variant="soft"
                                                onClick={() => setSelectedQuiz(quiz)}
                                            >Attempt</Button>
                                        )}
                                    </Flex>
                                ))}
                            </Box>
                        </Flex>
                    </Card>

                    {/* Progress and Lessons Card */}
                    <Card style={{ flex: 1 }}>
                        <Flex direction="column" gap="4">
                            {/* Progress Chart */}
                            <Box style={{ height: '200px' }}>
                                <Text size="3" mb="3" weight="bold">Progress</Text>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData}>
                                        <XAxis
                                            dataKey="name"
                                            angle={-45}
                                            textAnchor="end"
                                            height={70}
                                            interval={0}
                                            style={{ fontSize: '8px' }}
                                        />
                                        <YAxis
                                            domain={[0, 100]}
                                            style={{ fontSize: '12px' }}
                                            unit="%"
                                        />
                                        <Tooltip
                                            contentStyle={{ fontSize: '12px' }}
                                        />
                                        {/* <Legend
                                            formatter={(value, entry) => entry.payload.name} // Show full lesson title
                                            wrapperStyle={{ paddingTop: '10px' }}
                                        /> */}
                                        <Line
                                            type="monotone"
                                            dataKey="score"
                                            stroke="#818CF8"
                                            strokeWidth={2}
                                            dot={{ fill: '#818CF8' }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>

                            {/* Lessons List */}
                            <Box>
                                <Text size="3" mb="3" weight="bold">Lessons</Text>
                                {lessons.map((lesson) => (
                                    <Box key={lesson.id} mb="2">
                                        <Flex justify="between" mb="1">
                                            <Text size="2">{lesson.title}</Text>
                                            <Text size="2">{lesson.completion_percentage}%</Text>
                                        </Flex>
                                        <Box style={{
                                            height: '8px',
                                            backgroundColor: '#F3F4F6',
                                            borderRadius: '4px',
                                            overflow: 'hidden'
                                        }}>
                                            <Box style={{
                                                width: `${lesson.completion_percentage}%`,
                                                height: '100%',
                                                backgroundColor: '#818CF8',
                                                borderRadius: '4px'
                                            }} />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Flex>
                    </Card>
                </Grid>
            </Flex>
            {selectedQuiz && (
                <AlertDialog.Root open={true}>
                    <AlertDialog.Content maxWidth="450px" aria-describedby="alert-dialog-description">
                        {/* <AlertDialog.Title>Quiz</AlertDialog.Title> */}
                        <StudentQuiz summary={selectedQuiz} onComplete={handleQuizComplete} />
                    </AlertDialog.Content>
                </AlertDialog.Root>
            )}
        </Box>
    );
};

export default CourseProgress; 